// Companion/ChatFollowups.tsx
import { ArrowUpRight, ListChecks } from '@phosphor-icons/react';
import { capture } from '@/utils/analytics';
import type { ChatFollowup } from '@/types/chatStream';
import ChatChip, { ChatChipRow } from './ChatChip';
import type { ChatQuestionFollowup } from './blocks/chatQuestionFollowup';

/** Chips shown under an answer. Two choices stay useful without becoming a menu. */
export const MAX_CHAT_FOLLOWUPS = 2;

/** Each chip enters a beat after the one before it. */
const CHIP_STAGGER_MS = 90;

export interface ChatFollowupsProps {
  /** The turn's follow-ups, exactly as the server sent them. */
  followups: ChatFollowup[];
  /** For the click event; the conversation the answer belongs to. */
  conversationId: string;
  /** For the click event; the turn that offered these chips. */
  turnId: string;
  /** Send this prompt as the next question, through the one send path. */
  onSend: (prompt: string, anchor?: ChatQuestionFollowup['anchor']) => void;
  /** The question this answer's chips should remain grounded to. */
  question?: ChatQuestionFollowup;
}

const MISSED_QUESTION_FOLLOWUPS: ChatFollowup[] = [
  {
    id: 'missed-question-why',
    kind: 'prompt',
    label: 'Why did I miss this?',
    prompt: 'Why did I miss this practice question?',
  },
  {
    id: 'missed-question-concept',
    kind: 'prompt',
    label: 'Explain the core concept simply',
    prompt: 'Explain the core concept this practice question tests simply.',
  },
  {
    id: 'missed-question-quiz',
    kind: 'prompt',
    label: 'Quiz me on this concept again',
    prompt: 'Quiz me again on the core concept this practice question tests.',
  },
];

/**
 * What a chip leads with. A study chip is asking Ora for practice rather than
 * for another explanation (ORA-827), so it takes the glyph the action card
 * already uses for a QBank block, and the question chips keep their arrow.
 */
const followupIcon = (followup: ChatFollowup) => {
  const isStudy = followup.kind === 'prompt' && followup.intent === 'study';
  const Icon = isStudy ? ListChecks : ArrowUpRight;

  return (
    <Icon
      size={14}
      aria-hidden="true"
      data-testid={isStudy ? 'followup-study-icon' : 'followup-prompt-icon'}
    />
  );
};

/**
 * The next questions an answer offers (ORA-606).
 *
 * They come from the model's own `suggest_followups` tool and from nowhere
 * else: an answer the model suggested nothing for shows nothing, because a
 * chip Ora did not choose is a question about "this" that the student never
 * asked. Only `kind: 'prompt'` is drawn — a navigate chip would be a second
 * kind of thing in the same row, and nothing has produced one for a long time.
 *
 * One of them may ask for practice instead of for another explanation (ORA-827).
 * It is the same chip doing the same thing — its prompt is sent as the next
 * question — and only its glyph says which it is.
 *
 * On a phone the chips are one sideways-scrolling line (ORA-628): a
 * wrapped block of them pushed the answer they belong to off the screen.
 *
 * Purely presentational beyond the analytics: the caller decides *when* an
 * answer may offer chips (the last one, once its turn is done, while the
 * composer is empty) and what sending one means.
 */
const ChatFollowups = ({
  followups,
  conversationId,
  turnId,
  onSend,
  question,
}: ChatFollowupsProps) => {
  const continueFollowup = followups.find(
    (followup) =>
      followup.kind === 'prompt' &&
      followup.label === 'Continue' &&
      followup.prompt === 'continue'
  );

  const prompts = (
    question && !question.firstAttemptCorrect
      ? [
          // ORA-1116: a multipart answer's server-owned next step stays first;
          // missed-question shortcuts fill only the remaining slot.
          ...(continueFollowup ? [continueFollowup] : []),
          ...MISSED_QUESTION_FOLLOWUPS,
        ]
      : followups
  )
    .filter((followup) => followup.kind === 'prompt')
    .slice(0, MAX_CHAT_FOLLOWUPS);

  if (prompts.length === 0) return null;

  return (
    <ChatChipRow
      className="chat-motion mt-2 flex-wrap"
      role="group"
      aria-label="Follow-up questions"
      data-testid="chat-followups"
    >
      {prompts.map((followup, index) => (
        <ChatChip
          key={followup.id}
          className="animate-chat-fade-up"
          style={{ animationDelay: `${index * CHIP_STAGGER_MS}ms` }}
          icon={followupIcon(followup)}
          onClick={() => {
            capture('companion_followup_click', {
              conversationId,
              turnId,
              index,
              label: followup.label,
              // Only on the chips that have one, so the events every other chip
              // has been sending are unchanged.
              ...(followup.kind === 'prompt' && followup.intent
                ? { intent: followup.intent }
                : {}),
            });

            onSend(followup.prompt, question?.anchor);
          }}
        >
          {followup.label}
        </ChatChip>
      ))}
    </ChatChipRow>
  );
};

export default ChatFollowups;
